import React, { useState, useMemo, useEffect } from "react";
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import { isDirty, getFormValues, getFormInitialValues } from "redux-form";
import {
	Modal,
	Button,
	ProgressIndicator
} from "rhombus-react";

import useRefinedData from "hooks/useRefinedData";
import useFramingCheck from "hooks/useFramingCheck";

import { isFrameAdjusted, getRefinedPreviewContent } from "utils/Refinements";
import {
	API_URL,
	TIA_DOMAIN,
	VERSION_STATUS,
	DYNAMIC_PREROLL
} from "utils/constants";
import { isMaxDynamicSocialVersions } from "utils/common";
import { getPreviewContent } from "reducers/Refinements";
import { EDIT_VERSION_BLADE_FORM } from "widgets/forms/list";
import InfoBanner from "widgets/InfoBanner";
import AdjustFramingBlade from "widgets/modals/versions/refinement/AdjustFramingBlade";

import "./EditVersionPreview.css";

export function buildPreviewURL({ libraryId, creativeId, versionId }) {
	const base = `${TIA_DOMAIN}/basic/${creativeId}?editable=true`;
	const encoded = encodeURIComponent(`${API_URL}/crm/v1/ui/creative-libraries/${libraryId}/creatives/${creativeId}/versions/${versionId}/s3variables?dirty=true`);

	return `${base}&dynamicAdsUrl=${encoded}`;
}

export default function EditVersionPreview(props) {
	const {
		commonModalProps,
		navigationActions,
		libraryId,
		versionInfo,
		saveVersion,
		saveAsVersion,
		pushModal,
	} = props;
	const {
		name,
		status,
		variableConfig,
		id,
		creative,
	} = versionInfo;
	const { popModal } = navigationActions;
	const { id: creativeId } = creative;
	const url = buildPreviewURL({ creativeId, libraryId, versionId: id });
	const routeFile = useSelector(state => state.creativeDetails.routeFile);
	const urlHtmlBase = `assetmanager/${libraryId}/${creativeId}`;
	const urlHtml = `asset-manager/${libraryId}/${creativeId}/${routeFile}`;
	const iframeBaseUrl = `${TIA_DOMAIN}/${urlHtmlBase}`;
	const dispatch = useDispatch();
	const initialValues = useSelector(getFormInitialValues(EDIT_VERSION_BLADE_FORM)) || {};
	const values = useSelector(getFormValues(EDIT_VERSION_BLADE_FORM)) || {};
	const framesAdjusted = useMemo(() => isFrameAdjusted(values, initialValues), [values, initialValues]);
	const [isLoading, setIsLoading] = useState(true);
	const [isSaving, setIsSaving] = useState(false);
	const shouldDisplayBanner = status === VERSION_STATUS.LIVE;
	const hasEdits = useSelector(isDirty(EDIT_VERSION_BLADE_FORM));
	const items = useSelector(state => state.creativeVersions.items);
	const creativeDetails = useSelector(state => state.creativeDetails);
	const disableSaveButtons = isSaving || !hasEdits;
	const [previewContent, setPreviewContent] = useState();
	const { isLoading: areRefinementsLoading, refinedDataMap } = useRefinedData(values, initialValues, libraryId, !framesAdjusted);

	const { width, height } = useSelector(state => ({
		width: state.creativeDetails.width,
		height: state.creativeDetails.height,
	}));


	const refinedPreviewContent = useMemo(() => getRefinedPreviewContent({
		previewContent,
		refinedDataMap,
		iframeBaseUrl,
		routeFile,
		allRefinementsLoaded: !areRefinementsLoading,
	}), [
		previewContent,
		refinedDataMap,
		iframeBaseUrl,
		routeFile,
		areRefinementsLoading
	]);

	useEffect(() => {
		if (framesAdjusted) {
			setIsLoading(true);
			// Get the refined preview content
			dispatch(getPreviewContent(urlHtml)).then((response) => {
				if (response?.success) {
					setPreviewContent(response?.payload?.data);
				}
			});
		}
	}, [framesAdjusted, urlHtml, dispatch]);

	useEffect(() => {
		if (framesAdjusted && refinedPreviewContent) {
			setIsLoading(false);
		}
	}, [framesAdjusted, refinedPreviewContent, setIsLoading]);

	function adjustFraming() {
		function Configured(props) {
			let modalProps = {
				...props,
				variableConfig,
				libraryId,
				versionInfo,
				isOnTopOfPreviewBlade: true,
				commonModalProps: {
					...commonModalProps,
				},
			};

			return <AdjustFramingBlade {...modalProps} />;
		}

		pushModal(Configured);
	}

	function closeModal() {
		popModal();
	}

	function onSave(values) {
		setIsSaving(true);
		saveVersion(values);
	}

	function Content() {
		const srcDocProp = framesAdjusted ? { srcDoc: refinedPreviewContent } : {};
		return (
			<div className="main-content">
				<ProgressIndicator isLoading={isLoading || isSaving} />
				{shouldDisplayBanner && <InfoBanner warning={{ description: "Saving will update the current live version." }} />}
				<iframe
					src={url}
					frameBorder="0"
					scrolling="no"
					width={width}
					height={height}
					onLoad={() => { setIsLoading(false); }}
					{...srcDocProp}
				/>
			</div>
		);
	}

	const showAdjustFramingButton = useFramingCheck(creativeDetails, variableConfig, [DYNAMIC_PREROLL]);


	function Footer() {
		return (
			<>
				<Button theme="default" className="cypress-preview-version-blade-back" onClick={closeModal}>Back</Button>
				<div className="footer-actions">
					{showAdjustFramingButton && <Button
						className="cypress-preview-open-smart-framing-blade"
						onClick={adjustFraming}>Adjust Framing</Button>}
					<Button
						disabled={disableSaveButtons || isMaxDynamicSocialVersions(items.length, creativeDetails)}
						className="cypress-preview-version-save-as"
						onClick={saveAsVersion}>Save As...</Button>
					<Button
						disabled={disableSaveButtons}
						theme="green"
						className="cypress-preview-save-version"
						onClick={() => onSave(values)}>Save Version</Button>
				</div>
			</>
		);
	}

	let modalProps = {
		...commonModalProps,
		title: `Preview Version - ${name}`,
		className: "edit-version-preview-blade",

		renderContent: Content,
		renderFooter: Footer,

		onClose: closeModal,
	};

	return <Modal {...modalProps} />;
}

EditVersionPreview.displayName = "EditVersionPreview";
EditVersionPreview.propTypes = {
	versionInfo: PropTypes.shape({
		name: PropTypes.string,
		status: PropTypes.string,
		variableConfig: PropTypes.object,
		id: PropTypes.number,
		crative: PropTypes.shape({
			id: PropTypes.number,
		}),
	}).isRequired,
	saveVersion: PropTypes.func.isRequired,
	saveAsVersion: PropTypes.func.isRequired,
	pushModal: PropTypes.func.isRequired,
};
